import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ZapIcon, AwardIcon } from 'lucide-react';
export function Doctor() {
  const {
    t
  } = useLanguage();
  const pillars: {
    title: string;
    body: string;
  }[] = t('doctor.pillars');
  const badges: {
    value: string;
    label: string;
  }[] = t('doctor.badges');
  const team: {
    name: string;
    credential: string;
    role: string;
    bio: string;
    initials: string;
    photo: string;
  }[] = t('doctor.team');
  const easeOutCubic = [0.33, 1, 0.68, 1];
  const featureIcons = [ShieldCheckIcon, ZapIcon, AwardIcon];
  return <section id="doctor" className="py-20 lg:py-28 bg-white overflow-hidden" data-id="element-98" data-mp="2vvrckriuyxx4">
      <div className="container mx-auto px-6" data-id="element-99" data-mp="2r1df8jkmygpt">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-stretch max-w-6xl mx-auto" data-id="element-100" data-mp="23d3wjqipy92w">
          {/* Institutional visual column — dense, no portrait */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1,
          ease: easeOutCubic
        }} className="relative" data-id="element-101" data-mp="1ye0t2aie0ax1">
            {/* Decorative offset frame */}
            <div className="absolute -top-5 -right-5 lg:-top-6 lg:-right-6 w-full h-full rounded-[2rem] border border-brand-gold/30 -z-10" data-id="element-102" data-mp="1ngb4dldl1t2h"></div>
            <div className="absolute -bottom-5 -left-5 lg:-bottom-6 lg:-left-6 w-28 h-28 bg-brand-blush/40 rounded-full blur-3xl -z-10" data-id="element-103" data-mp="1wfep0tseegj3"></div>

            <div className="rounded-[2rem] overflow-hidden relative z-10 bg-gradient-to-br from-brand-cream via-brand-stone/30 to-brand-blush/30 p-8 lg:p-10 h-full flex flex-col" data-id="element-104" data-mp="21fat0t13i7yn">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-3 mb-5" data-id="element-105" data-mp="3cs3ghrmusa8w">
                <span className="w-8 h-px bg-brand-gold" data-id="element-106" data-mp="2n4q02exit9q5"></span>
                <span className="text-brand-dark/50 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-107" data-mp="2n7c2k05k0z1i">
                  Lipo Laser · Beverly Hills
                </span>
              </div>

              {/* Editorial headline */}
              <p className="font-serif text-[1.75rem] lg:text-[2.25rem] font-light text-brand-dark leading-[1.15] italic mb-8" data-id="element-108" data-mp="2s4cdoph9grzh">
                Naveed N. Nosrati MD.
                <br data-id="element-109" data-mp="1zfydq46m3lla" />
                Precisión de
                <span className="text-brand-gold not-italic" data-id="element-110" data-mp="1zh9erw8oaf8p">
                  {' '}
                  cirugía plástica.
                </span>
              </p>

              {/* Feature list — fills the dead space */}
              <ul className="space-y-4 mb-8 pb-8 border-b border-brand-dark/10 flex-1" data-id="element-111" data-mp="29ctb2jr5ffxz">
                {pillars.map((pillar, idx) => {
                const Icon = featureIcons[idx] || ShieldCheckIcon;
                return <li key={idx} className="flex items-start gap-3" data-id="element-112" data-mp="3sd2b746ctley" data-mpi={idx}>
                      <Icon className="w-4 h-4 text-brand-gold shrink-0 mt-1" strokeWidth={1.5} data-id="element-113" data-mp="3sapl23qg04qt" />
                      <span className="text-sm text-brand-dark/75 font-light leading-snug" data-id="element-114" data-mp="3ifxkcr1kd1rt">
                        {pillar.title}
                      </span>
                    </li>;
              })}
              </ul>

              {/* Institutional badges grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-5" data-id="element-115" data-mp="jvxpqm6ucw57">
                {badges.map((badge, idx) => <div key={idx} className="flex flex-col" data-id="element-116" data-mp="5wfx1vqfcyqk" data-mpi={idx}>
                    <span className="font-serif text-2xl lg:text-[1.75rem] text-brand-dark font-light leading-none mb-1.5" data-id="element-117" data-mp="9yjdkyryatip">
                      {badge.value}
                    </span>
                    <span className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/50 leading-tight" data-id="element-118" data-mp="2up1ysbnpb2t0">
                      {badge.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Floating institutional mini-card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.9,
            delay: 0.5,
            ease: easeOutCubic
          }} className="absolute -bottom-5 right-4 lg:right-6 bg-brand-cream/95 backdrop-blur-md border border-brand-stone/40 rounded-2xl px-5 py-3 z-20 shadow-sm" data-id="element-119" data-mp="315u0bylw183h">
              <div className="flex items-center gap-3" data-id="element-120" data-mp="28i7mlxiy4n2w">
                <span className="w-6 h-px bg-brand-gold" data-id="element-121" data-mp="28fldhtgepzik"></span>
                <p className="text-[10px] font-medium tracking-widest uppercase text-brand-dark/60" data-id="element-122" data-mp="24fb92l89jh5t">
                  Beverly Hills, CA · Board Certified Plastic Surgery
                </p>
              </div>
            </motion.div>
          </motion.div>

          {/* Text column */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1,
          delay: 0.2,
          ease: easeOutCubic
        }} className="flex flex-col justify-center" data-id="element-123" data-mp="sxp4ppjzjvi2">
            <div className="inline-flex items-center gap-3 mb-5" data-id="element-124" data-mp="1gouilz6d8dpq">
              <span className="w-8 h-px bg-brand-gold" data-id="element-125" data-mp="1glyka9qlvn0r"></span>
              <span className="text-brand-dark/50 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-126" data-mp="3dhwa63itxsyt">
                {t('doctor.eyebrow')}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-[2.75rem] font-serif font-light text-brand-dark mb-5 tracking-tight leading-[1.1]" data-id="element-127" data-mp="pm8raa3ak6tb">
              {t('doctor.heading')}
            </h2>

            <p className="text-base lg:text-lg text-brand-dark/70 font-light leading-relaxed mb-8" data-id="element-128" data-mp="ym5kmp0mltup">
              {t('doctor.subheading')}
            </p>

            <div className="space-y-6 pt-6 border-t border-brand-stone/50" data-id="element-129" data-mp="tp5g4ijfcz5p">
              {pillars.map((pillar, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.7,
              delay: 0.4 + idx * 0.12,
              ease: easeOutCubic
            }} className="flex gap-4" data-id="element-130" data-mp="13l8j1qm3p1tp" data-mpi={idx}>
                  <span className="font-serif text-lg text-brand-gold/70 font-light italic shrink-0 mt-1" data-id="element-131" data-mp="2aw2zs5876lv6">
                    0{idx + 1}
                  </span>
                  <div data-id="element-132" data-mp="1vyu6h5zc380t">
                    <h3 className="font-serif text-lg lg:text-xl text-brand-dark font-normal mb-1.5" data-id="element-133" data-mp="20y6qliytm0zp">
                      {pillar.title}
                    </h3>
                    <p className="text-brand-dark/65 font-light leading-relaxed text-sm" data-id="element-134" data-mp="1n57e7puc4mwd">
                      {pillar.body}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Surgical team band */}
        <div className="max-w-6xl mx-auto mt-20 lg:mt-28 pt-14 lg:pt-16 border-t border-brand-stone/50" data-id="element-135" data-mp="1i7ysjf9za0qd">
          <div className="flex flex-col items-center text-center mb-10 lg:mb-12" data-id="element-136" data-mp="14a0r1agr9ysa">
            <div className="inline-flex items-center gap-3 mb-4" data-id="element-137" data-mp="188zu0pixflh3">
              <span className="w-8 h-px bg-brand-gold" data-id="element-138" data-mp="1opaa2de98kc8"></span>
              <span className="text-brand-dark/50 font-medium tracking-[0.25em] text-[10px] uppercase" data-id="element-139" data-mp="1omo2weva06uj">
                {t('doctor.teamEyebrow')}
              </span>
              <span className="w-8 h-px bg-brand-gold" data-id="element-140" data-mp="1ep9mgf9cri28"></span>
            </div>
            <h3 className="font-serif text-2xl md:text-3xl lg:text-[2.25rem] font-light text-brand-dark tracking-tight leading-[1.15]" data-id="element-141" data-mp="19qq1ah2l2aax">
              {t('doctor.teamHeading')}
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8" data-id="element-142" data-mp="10yo2soc2r43a">
            {team.map((member, idx) => <motion.article key={member.name} initial={{
            opacity: 0,
            y: 28
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.8,
            delay: idx * 0.12,
            ease: easeOutCubic
          }} className="rounded-[2rem] border border-brand-stone/50 bg-brand-cream/40 p-6 lg:p-8 flex flex-col items-center text-center" data-id="element-143" data-mp="w0uxdxilie42" data-mpi={idx}>
                <div className="relative mb-6" data-id="element-144" data-mp="37mdo7rg4o41p">
                  <span className="absolute inset-0 rounded-full border border-brand-gold/40 scale-[1.08]" data-id="element-145" data-mp="29zy6qpnpb6e5"></span>
                  {member.photo ? <img src={member.photo} alt={member.name} loading="lazy" className="w-32 h-32 lg:w-36 lg:h-36 rounded-full object-cover object-top select-none" draggable={false} data-id="element-146" data-mp="2ezkh8rc5a8j9" /> : <span className="w-32 h-32 lg:w-36 lg:h-36 rounded-full bg-white border border-brand-stone/60 flex items-center justify-center font-serif text-3xl lg:text-4xl font-light text-brand-gold" data-id="element-147" data-mp="1r7m0hrtxfkyq">
                      {member.initials}
                    </span>}
                </div>

                <h4 className="font-serif text-xl lg:text-2xl font-light text-brand-dark leading-tight mb-2" data-id="element-148" data-mp="hphz7a6p1ef9">
                  {member.name}
                </h4>
                <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-brand-gold mb-4" data-id="element-149" data-mp="mm8o8n0h7kct">
                  {member.role}
                </p>
                <p className="text-sm text-brand-dark/65 font-light leading-relaxed mb-5" data-id="element-150" data-mp="3q48qrmn9ixj1">
                  {member.bio}
                </p>
                <div className="mt-auto pt-4 border-t border-brand-dark/10 w-full flex items-center justify-center gap-2" data-id="element-151" data-mp="3v21so3hj95di">
                  <ShieldCheckIcon className="w-3.5 h-3.5 text-brand-gold shrink-0" strokeWidth={1.5} data-id="element-152" data-mp="32fh0x1x5ar9f" />
                  <span className="text-[11px] text-brand-dark/60 font-light leading-snug" data-id="element-153" data-mp="37czamuizlaih">
                    {member.credential}
                  </span>
                </div>
              </motion.article>)}
          </div>
        </div>
      </div>
    </section>;
}